import { useEffect, useMemo, useRef, useState } from 'react'
import {
  type CompetenciesApiArg,
  type ManagersApiArg,
  type ResponseDtoCompetency,
  type ResponseDtoUser,
  useCompetenciesQuery,
  useManagersQuery
} from '../../../../API/rtk-query-api/store/tmsApi'
import { filtersActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { type DropdownOption, MultiSelectDropdown } from '../../../UI/multi-select-dropdown/MultiSelectDropdown'
import './FilterOptions.scss'
import Checkbox, { type CheckboxChangeEvent } from '../../../UI/checkbox/Checkbox'

const FilterOptionsTM = () => {
  const dispatch = useAppDispatch()
  const trainingMatrixFilters = useAppSelector(state => state.trainingMatrixFilters)

  const [competencySettings, setCompetencySettings] = useState<CompetenciesApiArg>({ page: 1 })
  const [competencyData, setCompetencyData] = useState<ResponseDtoCompetency>()

  const [managerSettings, setManagerSettings] = useState<ManagersApiArg>({ page: 1 })
  const [managerData, setManagerData] = useState<ResponseDtoUser>()

  const setDataAsSelected = useRef(false)

  const { data: competencies, isFetching: isFetchingCompetencies } = useCompetenciesQuery(competencySettings)
  const { data: managers, isFetching: isFetchingManagers } = useManagersQuery(managerSettings)

  useEffect(() => {
    if (competencies && competencies.data && !isFetchingCompetencies) {
      if ((competencies?.page ?? 0) > 1) {
        setCompetencyData({
          ...competencyData,
          data: [...(competencyData?.data ?? []), ...(competencies.data ?? [])]
        })
      } else {
        setCompetencyData(competencies)
      }
    }
  }, [competencies, isFetchingCompetencies])

  useEffect(() => {
    if (setDataAsSelected.current) {
      dispatch(
        filtersActions.setSelectedCompetences(
          competencyData?.data?.map(competency => {
            return { value: competency.id ?? '', text: competency.name ?? '' }
          }) ?? []
        )
      )

      setDataAsSelected.current = false
    }
  }, [competencyData])

  useEffect(() => {
    if (managers && managers.data && !isFetchingManagers) {
      if ((managers.page ?? 0) > 1) {
        setManagerData({
          ...managerData,
          data: [...(managerData?.data ?? []), ...(managers.data ?? [])]
        })
      } else {
        setManagerData(managers)
      }
    }
  }, [managers, isFetchingManagers])

  useEffect(() => {
    if (setDataAsSelected.current) {
      dispatch(
        filtersActions.setSelectedManagers(
          managerData?.data?.map(manager => {
            return { value: manager.id ?? '', text: manager.fullName ?? '' }
          }) ?? []
        )
      )

      setDataAsSelected.current = false
    }
  }, [managerData])

  const competencyDropdownOptions = useMemo(
    () =>
      competencyData?.data?.map(competency => {
        return { value: competency.id ?? '', text: competency.name ?? '' }
      }) ?? [],
    [competencyData]
  )

  const managerDropdownOptions = useMemo(() => {
    return (
      managerData?.data?.map(manager => {
        return { value: manager.id ?? '', text: manager.fullName ?? '' }
      }) ?? []
    )
  }, [managerData?.data])

  // Competencies
  const handleSelectCompetency = (selection: Array<DropdownOption>) => {
    dispatch(filtersActions.setSelectedCompetences(selection))
  }

  const handleSelectAllCompetencies = () => {
    if (competencies?.maxPages ?? 1 > 1) {
      setCompetencySettings({ ...competencySettings, page: undefined })
      setDataAsSelected.current = true
    } else {
      dispatch(filtersActions.setSelectedCompetences(competencyDropdownOptions))
    }
  }

  const handleReachCompetenciesBottom = () => {
    if ((competencySettings.page ?? 0) < (competencies?.maxPages ?? 0)) {
      setCompetencySettings({ ...competencySettings, page: (competencySettings.page ?? 0) + 1 })
    }
  }

  const handleCompetenciesFilter = (filter: string) => {
    setCompetencySettings({ filter: filter, page: 1 })
  }

  //Managers
  const handleSelectManager = (selection: Array<DropdownOption>) => {
    dispatch(filtersActions.setSelectedManagers(selection))
  }

  const handleSelectAllManagers = () => {
    if (managers?.maxPages ?? 0 > 1) {
      setManagerSettings({ ...managerData, page: undefined })
      setDataAsSelected.current = true
    } else {
      dispatch(filtersActions.setSelectedManagers(managerDropdownOptions))
    }
  }

  const handleReachManagerDropdownBottom = () => {
    if ((managerSettings.page ?? 0) < (managers?.maxPages ?? 0)) {
      setManagerSettings({ ...managerSettings, page: (managerSettings.page ?? 0) + 1 })
    }
  }

  const handleManagerFilter = (filter: string) => {
    setManagerSettings({ filter: filter, page: 1 })
  }

  //Checkbox
  const handleShowAllChange = (event?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setShowAllReports(event?.checked ?? false))
  }

  const handleShowOtherCompetenciesChange = (event?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setShowOtherCompetencies(event?.checked ?? false))
  }

  const handleChangeShowDesiredCompetencies = (event?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setShowDesirecCompetencies(event?.checked ?? false))
  }

  return (
    <div className='filter-options'>
      <MultiSelectDropdown
        options={competencyDropdownOptions}
        values={trainingMatrixFilters.selectedCompetencies}
        onChange={handleSelectCompetency}
        label='Competency'
        placeholder='Select competency'
        totalOptions={competencyData?.total}
        onLazyLoad={handleReachCompetenciesBottom}
        onFilter={handleCompetenciesFilter}
        onSelectAll={handleSelectAllCompetencies}
        appendTo='self'
        fullWidth
      />
      <MultiSelectDropdown
        options={managerDropdownOptions}
        values={trainingMatrixFilters.selectedManagers}
        onChange={handleSelectManager}
        label='Manager'
        placeholder='Select manager'
        totalOptions={managerData?.total}
        onLazyLoad={handleReachManagerDropdownBottom}
        onFilter={handleManagerFilter}
        onSelectAll={handleSelectAllManagers}
        appendTo='self'
        fullWidth
      />
      <Checkbox
        name='showAll'
        onChange={handleShowAllChange}
        checked={trainingMatrixFilters.showAllReports}
        label='Show All Reports'
      />
      <Checkbox
        name='desired'
        onChange={handleChangeShowDesiredCompetencies}
        checked={trainingMatrixFilters.showDesiredCompetencies}
        label='Show Desirable Competencies'
      />
      <Checkbox
        name='showOther'
        onChange={handleShowOtherCompetenciesChange}
        checked={trainingMatrixFilters.showOtherCompetencies}
        label='Show Other Competencies'
      />
    </div>
  )
}

export default FilterOptionsTM
